import { useState, useMemo } from "react";

export interface ContractFilters {
  search: string;
  contractStatus: string[];
  paymentStatus: string[]; // Kept as paymentStatus for UI consistency, but maps to contract.status
  dateStart: string;
  dateEnd: string;
  // Digits only — the API `parseInt`s it and ignores the filter otherwise. Either
  // typed in the filter bar, or preset when the page is opened on a single contract
  // from a coding row on the Codings page.
  contractId: string;
}

export interface ContractFilterOptions {
  contractStatuses: string[];
  paymentStatuses: string[]; // Maps to contract.status values
}

interface Contract {
  id: number;
  status: string; // 'non facturé', 'facturé', 'payé'
  contractStatus: string; // 'unsigned', 'signed', 'sent_for_signature', 'viewed', 'declined', 'cancelled'
  createdAt?: string; // Date de création du contrat
  factureId?: number;
  facture?: any;
  cancellationReason?: string;
  cancelledAt?: string;
  codages: Array<{
    id: number;
    aia_codage_Date: string;
    aia_codage_type: string;
    aia_game_name: string;
    coder: {
      first_name: string;
      last_name: string;
    };
    game: {
      gameName: string;
      metadata?: {
        match_date?: string;
      };
    };
  }>;
  supervisions: Array<{
    id: number;
    aia_supervision_date: string;
    aia_supervision_type: string;
    aia_game_name: string;
    supervisor: {
      first_name: string;
      last_name: string;
    };
    coder: {
      first_name: string;
      last_name: string;
    };
    game: {
      gameName: string;
      metadata?: {
        match_date?: string;
      };
    };
  }>;
}

const ALL_CONTRACT_STATUSES = [
  "pending",
  "no_contract",
  "unsigned",
  "sent_for_signature",
  "viewed",
  "signed",
  "declined",
  "cancelled",
];

const ALL_PAYMENT_STATUSES = ["non facturé", "facturé", "payé"];

// Filtres par défaut
const DEFAULT_FILTERS: ContractFilters = {
  search: "",
  // `viewed` sits alongside `sent_for_signature`: both describe a contract out for
  // signature, and leaving it out hid the ones the signer had opened.
  contractStatus: [
    "no_contract",
    "sent_for_signature",
    "viewed",
    "signed",
    "pending",
  ],
  paymentStatus: ["non facturé", "facturé"],
  dateStart: "",
  dateEnd: "",
  contractId: "",
};

/**
 * Filters showing exactly one contract.
 *
 * The status filters are emptied rather than left at their defaults, which exclude
 * `cancelled`, `declined`, `unsigned` and `payé` — a contract in one of those states
 * would otherwise not show up despite being asked for by id. Empty means "no
 * restriction" to the API, and keeps the two multi-selects at one line instead of
 * listing every status.
 */
const filtersForContract = (contractId: string): ContractFilters => ({
  ...DEFAULT_FILTERS,
  contractStatus: [],
  paymentStatus: [],
  contractId,
});

/**
 * Asks for every contract, whatever its state.
 *
 * A value rather than an empty parameter because the page's default filters are not
 * empty: they hide `cancelled`, `declined`, `unsigned` and `payé`. A dashboard card
 * counting all 24 contracts that linked to the bare page would land on the 17 the
 * default view shows, so the total and the list it opens have to say so explicitly.
 */
export const ALL_CONTRACT_STATUSES_PARAM = "all";

/**
 * Filters restricted to the given contract statuses.
 *
 * How the dashboard links to a figure it shows: the card counts contracts in one
 * state, and the page it leads to has to show that same set. The payment filter is
 * emptied for the same reason `filtersForContract` empties it — its default excludes
 * `payé`, which would quietly drop rows the figure counted.
 */
const filtersForContractStatus = (statuses: string[]): ContractFilters => ({
  ...DEFAULT_FILTERS,
  contractStatus: statuses.includes(ALL_CONTRACT_STATUSES_PARAM)
    ? []
    : statuses,
  paymentStatus: [],
});

export const useContractFilters = (
  contracts: Contract[],
  initialContractId?: string,
  initialContractStatus?: string[],
) => {
  const [filters, setFilters] = useState<ContractFilters>(() => {
    if (initialContractId) return filtersForContract(initialContractId);
    if (initialContractStatus?.length)
      return filtersForContractStatus(initialContractStatus);
    return DEFAULT_FILTERS;
  });

  // Options fixes pour les filtres (indépendantes des contrats affichés)
  const filterOptions: ContractFilterOptions = useMemo(() => {
    return {
      contractStatuses: ALL_CONTRACT_STATUSES,
      paymentStatuses: ALL_PAYMENT_STATUSES,
    };
  }, []);

  const updateFilter = (
    key: keyof ContractFilters,
    value: string | string[],
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  /**
   * Sets the contract id filter, clearing the status filters along with it.
   *
   * An id identifies one contract, so narrowing it further by status only serves to
   * hide it: the defaults exclude `cancelled`, `declined`, `unsigned` and `payé`, so
   * typing the id of a cancelled contract used to return nothing while the same id in
   * the URL found it. Clearing the two multi-selects makes both routes agree, and the
   * chips visibly disappear, so the bar still shows what is applied.
   *
   * Emptying the id restores the default statuses rather than leaving the list
   * unrestricted, which would silently show contract states the page does not list by
   * default. A status selection made before typing an id is lost — accepted, since
   * the alternative is resurrecting a selection the user can no longer see.
   */
  const updateContractIdFilter = (contractId: string) => {
    setFilters((prev) => ({
      ...prev,
      contractId,
      contractStatus: contractId ? [] : DEFAULT_FILTERS.contractStatus,
      paymentStatus: contractId ? [] : DEFAULT_FILTERS.paymentStatus,
    }));
  };

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
  };

  const hasActiveFilters =
    filters.search !== "" ||
    filters.contractId !== "" ||
    filters.dateStart !== "" ||
    filters.dateEnd !== "" ||
    JSON.stringify([...filters.contractStatus].sort()) !==
      JSON.stringify([...DEFAULT_FILTERS.contractStatus].sort()) ||
    JSON.stringify([...filters.paymentStatus].sort()) !==
      JSON.stringify([...DEFAULT_FILTERS.paymentStatus].sort());

  return {
    filters,
    filterOptions,
    updateFilter,
    updateContractIdFilter,
    resetFilters,
    hasActiveFilters,
  };
};
